import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fetchAdEventAnalytics, fetchAds } from '../../utils/api';

const AdsReports = () => {
  const [ads, setAds] = useState([]);
  const [selectedAdId, setSelectedAdId] = useState('');
  const [rows, setRows] = useState([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadAds = async () => {
      setLoadingAds(true);
      setError('');
      try {
        const res = await fetchAds();
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setAds(data);
        if (data.length > 0) {
          setSelectedAdId(String(data[0].id));
        }
      } catch (err) {
        console.error('Error loading ads for analytics:', err);
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load your ads.');
      } finally {
        if (!cancelled) setLoadingAds(false);
      }
    };
    loadAds();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAnalytics = async () => {
    if (!selectedAdId) {
      setError('Select an ad first.');
      setRows([]);
      return;
    }
    setLoadingAnalytics(true);
    setError('');
    try {
      const res = await fetchAdEventAnalytics(selectedAdId);
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading ad analytics:', err);
      setError(err.response?.data?.message || 'Failed to load ad analytics.');
      setRows([]);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Ad Analytics
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        View event analytics for a particular ad.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            size="small"
            label="Select Ad"
            value={selectedAdId}
            onChange={(e) => setSelectedAdId(e.target.value)}
            disabled={loadingAds}
            sx={{ minWidth: { xs: '100%', sm: 360 } }}
          >
            {ads.map((ad) => (
              <MenuItem key={ad.id} value={String(ad.id)}>
                {ad.title || `Ad ${ad.id}`} (ID: {ad.id})
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={loadAnalytics}
            disabled={loadingAds || loadingAnalytics || !selectedAdId}
            startIcon={loadingAnalytics ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loadingAnalytics ? 'Loading...' : 'Load Analytics'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sub Ad ID</TableCell>
                <TableCell>Sub Ad Title</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Unique Users</TableCell>
                <TableCell>Total Event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingAds || loadingAnalytics ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" variant="body2">
                      No analytics found for the selected ad.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow key={`${row.subAdId}-${row.eventType}-${idx}`}>
                    <TableCell>{row.subAdId ?? '-'}</TableCell>
                    <TableCell>{row.subAdTitle || '-'}</TableCell>
                    <TableCell>{row.eventType || '-'}</TableCell>
                    <TableCell>{row.uniqueUsers ?? 0}</TableCell>
                    <TableCell>{row.totalEvent ?? 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AdsReports;
